import { ResizingMode, ContestStatus } from '@betterpool/api-types/contests-service';
import type { UserContest } from '@betterpool/api-types/contests-service';
import useTranslation from 'next-translate/useTranslation';
import React, { useMemo, useCallback } from 'react';
import { Group, createStyles, useMantineTheme } from '@mantine/core';

import { BadgeResizable } from '~/components/shared/Badges/Badges';
import { CurrencyConvertor } from '~/components/utils/formatters';
import type { MyUserContest } from '~/domains/contest/domains/myContests/types/userContest';
import type { Contest } from '~/domains/contest/domains/services/interfaces/contest.interface';
import getContestMaxEntriesLabel from '~/domains/contest/domains/gamecenter/domains/common/utils/getContestMaxEntriesLabel';

import { useAdditionalPayoutSettings } from '../../../hooks/useAdditionalPayoutSettings';
import PrizeBadge from '../../PrizeBadge/PrizeBadge';
import type { ContestCardActionBanner } from '../types';

import ContestCardFooterMeta from './ContestCardFooterMeta';

const useStyles = createStyles((theme) => ({
  prizes: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.other.spacing._2,
    justifyContent: 'end',
  },
}));

type ContestMetaGenericCommissionerProps = {
  contest: Contest | MyUserContest;
  actionBanner: ContestCardActionBanner;
  isEntertainmentOnly?: boolean;
  survivor?: UserContest['survivor'];
  isSurvivor: boolean;
};

function ContestMetaGenericCommissioner({
  actionBanner,
  isEntertainmentOnly,
  contest,
  survivor,
  isSurvivor,
}: ContestMetaGenericCommissionerProps) {
  const { t } = useTranslation('contest');
  const { classes } = useStyles();
  const theme = useMantineTheme();

  const {
    type,
    isEnabled: isGuaranteedPlusEnabled,
    hasPrizeIncreased,
  } = useAdditionalPayoutSettings(contest);

  const maxEntries = useMemo(() => getContestMaxEntriesLabel(contest.entries), [contest.entries]);
  // TODO: Replace this placeholder with actual missing picks calculation once the data is available
  // @ts-expect-error - missingPicks property doesn't exist yet but will be added to EntriesDetails interface
  const missingPicks = contest?.entries?.missingPicks || 0;

  const getScheduledMeta = useCallback(
    () =>
      [
        {
          value: `${contest.entries.filled}/${maxEntries}`,
          label: (
            <Group spacing={theme.other.spacing._2}>
              {t('common.contestCard.footer.entries')}
              {contest.resizing_mode === ResizingMode.RESIZABLE ? (
                <BadgeResizable iconSize={12} />
              ) : null}
            </Group>
          ),
          textAlign: 'flex-start',
        },
        contest?.entries?.max_per_user && {
          value: contest?.entries?.max_per_user.toString(),
          label: t('common.contestCard.footer.maxEntries'),
          textAlign: 'center',
        },
        !isEntertainmentOnly
          ? {
              value: CurrencyConvertor(contest.entry_fee / 100),
              label: t('common.contestCard.footer.entry'),
              textAlign: 'flex-end',
            }
          : {
              // Empty column to keep alignining the maxEntries column
              value: '',
              label: '',
              textAlign: 'flex-end',
            },
        // @TODO: Confirm with Product if this is still needed
        // !isEntertainmentOnly &&
        //   !showMaxEntries && {
        //     value: CurrencyConvertor(contest.prize_pool / 100),
        //     label: (
        //       <div className={classes.prizes}>
        //         {t('common.contestCard.footer.prizes')}
        //         <PrizeBadge
        //           resizingMode={contest.resizing_mode}
        //           isGuaranteedPlusEnabled={isGuaranteedPlusEnabled}
        //           additionalPayoutType={type}
        //           iconSize={12}
        //           style={{ marginLeft: 1 }}
        //         />
        //       </div>
        //     ),
        //     textAlign: 'flex-end',
        //     valueColor: hasPrizeIncreased ? theme.colors.gold[0] : undefined,
        //   },
      ].filter(Boolean),
    [
      contest.entries.filled,
      contest.entries.max_per_user,
      contest.entry_fee,
      contest.prize_pool,
      contest.resizing_mode,
      maxEntries,
      isEntertainmentOnly,
      t,
      classes.prizes,
      isGuaranteedPlusEnabled,
      type,
      hasPrizeIncreased,
    ]
  );

  const getInProgressMeta = useCallback(
    () =>
      [
        {
          value: contest.entries.filled.toString(),
          label: (
            <Group spacing={theme.other.spacing._2}>
              {t('common.contestCard.footer.entries')}
              {contest.resizing_mode === ResizingMode.RESIZABLE ? (
                <BadgeResizable iconSize={12} />
              ) : null}
            </Group>
          ),
          textAlign: 'flex-start',
        },
        isSurvivor && {
          value: survivor?.aliveEntries?.toString() || '0',
          label: t('common.contestCard.footer.entriesAlive'),
          textAlign: 'center',
        },
        isSurvivor && {
          value: (+contest.entries.filled - (survivor?.aliveEntries || 0)).toString(),
          label: t('common.contestCard.footer.entriesEliminated'),
          textAlign: 'center',
        },
        missingPicks > 0 && {
          value: missingPicks.toString(),
          label: t('common.contestCard.footer.missingPicks'),
          textAlign: 'flex-end',
          valueColor: theme.colors.red[3],
        },
      ].filter(Boolean),
    [
      contest.entries.filled,
      contest.prize_pool,
      contest.resizing_mode,
      isEntertainmentOnly,
      isSurvivor,
      survivor?.aliveEntries,
      t,
      classes.prizes,
      missingPicks,
    ]
  );

  const meta = useMemo(() => {
    switch (contest.status) {
      case ContestStatus.SCHEDULED:
        return getScheduledMeta();
      case ContestStatus.IN_PROGRESS:
        // TODO: Remove conditional when the data is available
        return missingPicks > 0 ? getInProgressMeta() : getScheduledMeta();
      default:
        return null;
    }
  }, [contest.status, getScheduledMeta, getInProgressMeta]);

  return <ContestCardFooterMeta actionBanner={actionBanner} meta={meta} />;
}

export default ContestMetaGenericCommissioner;
